import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import Decimal from "decimal.js";
import { Lock, Plus, Trash2, Wallet } from "lucide-react";
import { Button } from "../../components/Button";
import { quietControlClass } from "../../components/QuietField";
import { api } from "../../api/client";
import { formatMoney, toAmountString, toDecimal } from "../../lib/money";
import type { Money, PaymentTermInput, QuotePaymentTerm } from "../../api/types";

// 付款條件(設計規格 §2)。客戶要在報價單上看到「訂金 30% / 期中 40% / 尾款 30%」——
// 那是談生意時就談定的條件,所以它是報價單的一部分,不是發票的一部分。
//
// 四條規矩寫死在這裡:
//
// 1. **金額一律 decimal.js,絕不用 Number。** `100000 * 0.3333` 在 JavaScript 裡
//    不是你想的那個數字,而這是要印給客戶看的金額。
// 2. **尾差由 seq 最大的那一期吸收,但吸收有界限。** 三期各 33.33% 的話,前兩期
//    照 ROUND_HALF_UP 算,最後一期 = 總額 − 前面幾期的合計。不這樣做三期加起來
//    會比總額少十塊,而那十塊錢會讓財務對半天。**算出來是負的、比例合計已經
//    超過 100%、或最後一期明寫 0% 時一律不吸收** —— 一張印著負數的報價單,比一張
//    加不滿總額的報價單糟得多。這支換算是後端 `app/services/payment_terms.py`
//    的鏡像 —— 畫面上先看到的數字與存檔後回來的數字必須是同一個。
// 3. **合計不是 100% 只提示、不擋存檔**(§2.4)。寫到一半本來就會不等於 100%,
//    擋下來只會逼人先亂填一個數字。畫面上顯示合計與差額,但放行。
// 4. **三個常用組合只是填表,不是範本**(§2.6)。按下去就是把幾列填進去,
//    填完跟手打的一模一樣 —— 不存範本、不綁定,之後改總額也不會回頭參照它。
//    真的做成可儲存的範本,就要回答「範本改了,已經用它的單要不要跟著改」,
//    那個問題不值得為三個固定組合去回答。

/** 比例與金額之間的倍率。寫成 Decimal 是為了讓除法留在十進位裡。 */
const HUNDRED = new Decimal(100);

const ZERO = new Decimal(0);

/** 金額的小數位數(分)。與 `lib/money.ts` 的 `AMOUNT_DP`、後端的 `round2` 同一個數字。 */
function round2(value: Decimal): Decimal {
  return value.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
}

/**
 * 換算用得到的那幾欄。存在的理由與後端的 `payment_terms.Term` 一樣:
 * 換算是純函式,不該為了跑一條測試就去建一張報價單。
 */
export interface ResolvableTerm {
  seq: number;
  /** `null` = 沒填。**「0%」與「還沒填」是兩件事**:前者是講定這一期不收錢。 */
  percent: string | null;
  amount: string | null;
  /**
   * 後端算好並回來的本期金額。**已開票的那一期只有這裡讀得到真正的數字。**
   *
   * `PaymentTermOut` 的 validator 把比例期的 `amount` 一律清成 `null`
   * (那一欄存的是後端釘住的值,不是使用者填的),所以「已開票 30,000」
   * 這一列的 `amount` 送到前端就是空的 —— 拿它來算會得到 0,而尾差那一期
   * 會替它把整筆總額吸走,畫面因此比後端、PDF 與開票申請都少了一整期的錢。
   */
  resolved_amount: string;
  invoiced_amount: string;
}

/** 這一期已經開過發票了嗎?開過就永不重算、不可刪(§2.5)。 */
export function isInvoiced(term: { invoiced_amount: string }): boolean {
  return toDecimal(term.invoiced_amount).greaterThan(0);
}

/** 用比例填的期別。`percent` 有值就是 —— 與 `amount` 二擇一(§2.2)。 */
function isPercentBased(term: ResolvableTerm): boolean {
  return term.percent !== null;
}

/**
 * 哪一期吸收尾差?回傳它在陣列裡的索引,沒有就回 `null`。
 *
 * 是 **`seq` 最大**的那一期,不是陣列最後一筆:畫面上的列排得動,取成最後一筆
 * 的話,使用者把尾款那一列移到最上面,尾差就會落到訂金上 —— 而訂金是最先
 * 開出去的那一張發票。
 *
 * 三個例外不吸尾差,理由各不相同:最後一期是**填死金額**的,講定的就是那個
 * 數字;最後一期**明寫 0%**,那同樣是講定的(「0% 是使用者講定這一期不收錢」),
 * 0% 卻收到全額會在 PDF 上印成「尾款 0%  100,000」;**只有一期**時沒有
 * 「前面幾期」可以扣,一期 30% 卻收到 100% 的金額,是最難查的那一種錯。
 *
 * **算出來的金額合不合理不在這裡判斷**(那要先有金額):負數與「比例合計已經
 * 超過 100%」那兩道界限在 `resolveAmounts()` 裡。
 */
export function absorbsTheRemainder(terms: ResolvableTerm[]): number | null {
  if (terms.length < 2) return null;
  let last = 0;
  for (let index = 1; index < terms.length; index += 1) {
    // `>=` 讓 seq 撞號時取後面那一筆,與後端「併 index 當次要鍵」同一個答案
    if (terms[index].seq >= terms[last].seq) last = index;
  }
  const term = terms[last];
  if (!isPercentBased(term) || isInvoiced(term)) return null;
  // 明寫 0% 是「講定這一期不收錢」,與填死金額同一種講定。**「沒填」不算**:
  // 它是 null,上一行就擋掉了 —— 那一列還沒寫完,不是講定不收錢。
  if (toDecimal(term.percent).isZero()) return null;
  return last;
}

/**
 * 所有比例期的比例合計。**與畫面上那一行「比例合計 87%」同一個數字。**
 *
 * 金額期不入帳(它談的不是比例),已開票的期別照樣算進去 —— 它的比例仍然印在
 * 那一列上,使用者看到的合計就該含它。
 */
export function percentTotalOf(terms: ResolvableTerm[]): Decimal {
  return terms
    .filter((term) => term.percent !== null)
    .reduce((acc, term) => acc.plus(toDecimal(term.percent)), ZERO);
}

/** 單獨看這一期的金額,還沒吸收尾差。 */
function oneAmount(total: Decimal, term: ResolvableTerm): Decimal {
  if (isInvoiced(term)) {
    // 已開票的期別**讀後端算好的 `resolved_amount`**,不讀 `amount`:
    // 比例期的 `amount` 在回應裡一律是 null(見 `ResolvableTerm.resolved_amount`),
    // 讀它會得到 0,而那 0 會讓尾差那一期多吸走一整期的錢。
    // 這一欄與後端的 `resolve()` 是同一個數字,總額改了也不動它(§2.5)。
    return round2(toDecimal(term.resolved_amount));
  }
  if (!isPercentBased(term)) {
    // 金額期照填的數字走。兩欄都還沒填的那一列是 0 —— 寫到一半是常態,不是錯誤。
    return round2(toDecimal(term.amount));
  }
  return round2(total.times(toDecimal(term.percent)).div(HUNDRED));
}

/**
 * 每一期實際要收多少,順序與 `terms` **逐筆對齊**,固定兩位小數。
 *
 * 尾差吸收寫成「總額 − 前面幾期的合計」,而不是「先照比例算再補那幾分錢」:
 * 三期各 33.33% 的合計是 99.99%,少掉的那 0.01% 不是進位造成的,是使用者打不出
 * 三分之一。只有前者讓三期**逐分**等於總額。
 *
 * **吸收有兩道界限**(與後端 `resolve()` 逐字相同):算出來是負的,或比例合計
 * 已經超過 100%。任一成立就不吸收,那一期退回照自己的比例算 —— 50/60/30 會讓
 * 最後一期算出 −10,000,而那個負數會原樣印在客戶手上的 PDF 上;40/70 則是讓
 * 同一列印出互相矛盾的「70%」與「60,000」。差額留在「各期合計 ≠ 總額」上讓下面
 * 那行提示說話(§2.4),**一張印著負數的報價單比一張加不滿總額的糟得多**。
 *
 * 合計**不到** 100% 時照樣吸收:尾款在生意上就是「剩下的」。這裡擋的只有
 * 「已經超過」。
 */
export function resolveAmounts(total: Money, terms: ResolvableTerm[]): string[] {
  const grand = round2(toDecimal(total));
  const amounts = terms.map((term) => oneAmount(grand, term));
  const last = absorbsTheRemainder(terms);
  if (last !== null) {
    const others = amounts.reduce((acc, amount, index) => (index === last ? acc : acc.plus(amount)), ZERO);
    const remainder = round2(grand.minus(others));
    if (!remainder.isNegative() && percentTotalOf(terms).lessThanOrEqualTo(HUNDRED)) {
      amounts[last] = remainder;
    }
  }
  return amounts.map((amount) => amount.toFixed(2));
}

/* ---------- 畫面上的一列 ---------- */

/**
 * 編輯中的一期。**比例與金額留成使用者打的原字串**,不當場換成數字 ——
 * 打到一半的「33.」被收成「33」的話,接著打的 3 會黏成「333」,比例當場差十倍
 * 而且畫面上看不出異狀(與單價那一格同一個坑)。
 */
interface TermRow {
  /** React key。新增的列還沒有 id,靠它才不會在刪掉中間一列之後錯配輸入框。 */
  key: string;
  seq: number;
  name: string;
  percent: string;
  amount: string;
  due_date: string;
  /** 後端算好的本期金額。已開票的那一列靠它才顯示得出真正的數字。 */
  resolved_amount: string;
  invoiced_amount: string;
}

let nextKey = 0;

/**
 * 後端那一列在畫面上長什麼樣,**不含 React key**。
 *
 * 與 `toRow()` 分開,是因為「有沒有改過」要拿它跟編輯中的列逐欄比 ——
 * 兩邊各寫一次正規化(抹尾零、null 換空字串)的話,後端回來的「30.0000」
 * 會永遠比不上欄位裡的「30」,整個區塊就會一直宣稱有未存檔的修改。
 */
function toFields(term: QuotePaymentTerm): Omit<TermRow, "key"> {
  return {
    seq: term.seq,
    name: term.name ?? "",
    // 比例期的 amount 後端一律清成 null,這裡拿到的空字串就是「沒填」
    percent: term.percent === null || term.percent === undefined ? "" : trimDecimal(term.percent),
    amount: term.amount === null || term.amount === undefined ? "" : trimDecimal(term.amount),
    due_date: term.due_date ?? "",
    resolved_amount: term.resolved_amount ?? "0.00",
    invoiced_amount: term.invoiced_amount ?? "0.00",
  };
}

function toRow(term: QuotePaymentTerm): TermRow {
  nextKey += 1;
  return { key: `term-${term.id}-${nextKey}`, ...toFields(term) };
}

/**
 * 這幾列「有什麼待存的內容」。**只看送得出去的那五欄** ——
 * `resolved_amount` 與 `invoiced_amount` 是唯讀的推導值,總額一動它們就變,
 * 算進去的話光是改報價單金額就會讓付款條件宣稱自己被改過。
 *
 * 走 `JSON.stringify` 而不是自己接一個分隔字元:名稱是自由填的,任何拿來當分隔
 * 的字元都有人打得進去,而撞上的那一次會讓「改過」與「沒改過」比出一樣的結果。
 */
function fingerprint(rows: Array<Omit<TermRow, "key">>): string {
  return JSON.stringify(
    rows.map((row) => [row.seq, row.name, row.percent.trim(), row.amount.trim(), row.due_date]),
  );
}

function blankRow(seq: number, patch: Partial<TermRow> = {}): TermRow {
  nextKey += 1;
  return {
    key: `new-${nextKey}`,
    seq,
    name: "",
    percent: "",
    amount: "",
    due_date: "",
    resolved_amount: "0.00",
    invoiced_amount: "0.00",
    ...patch,
  };
}

/**
 * 抹掉後端補的尾隨零。比例是 `NUMERIC(7,4)`、金額是 `NUMERIC(14,2)`,回來是
 * 「30.0000」與「55000.00」;欄位裡出現那種寫法,跟稅率顯示 0.0500 一樣難讀
 * (業主 2026-09-11 講過同一件事)。走 decimal.js 的 `toFixed()`,不經過
 * `parseFloat` —— 那一趟會讓超過 JS number 精度的數字無聲地回不來。
 */
function trimDecimal(value: string): string {
  const text = String(value).trim();
  if (text === "") return "";
  return toDecimal(text).toFixed();
}

function toResolvable(row: TermRow): ResolvableTerm {
  return {
    seq: row.seq,
    percent: row.percent.trim() === "" ? null : row.percent.trim(),
    amount: row.amount.trim() === "" ? null : row.amount.trim(),
    resolved_amount: row.resolved_amount,
    invoiced_amount: row.invoiced_amount,
  };
}

/**
 * 重新編號成 1…N。
 *
 * **有任何一期開過發票就整批不動。** `seq` 是後端認得的鍵,而且那個期數已經
 * 跟著發票寄出去了 —— 刪掉前面一列就把「第 2 期」變成「第 1 期」的話,送上去
 * 的整批取代會把已開票的那一期算成「要刪掉」,換來一個 409,而使用者只是
 * 刪了另外一列。
 */
function renumber(rows: TermRow[]): TermRow[] {
  if (rows.some(isInvoiced)) return rows;
  return rows.map((row, index) => (row.seq === index + 1 ? row : { ...row, seq: index + 1 }));
}

/** 三個常用組合(§2.6)。只是幾列資料,按下去就填進去,不存範本也不綁定。 */
const PRESETS: Array<{ label: string; rows: Array<{ name: string; percent: string }> }> = [
  { label: "一次付清", rows: [{ name: "一次付清", percent: "100" }] },
  {
    label: "訂金三成、尾款七成",
    rows: [
      { name: "訂金", percent: "30" },
      { name: "尾款", percent: "70" },
    ],
  },
  {
    label: "前中後三期",
    rows: [
      { name: "訂金", percent: "30" },
      { name: "期中款", percent: "40" },
      { name: "尾款", percent: "30" },
    ],
  },
];

/**
 * 那一列在 DOM 上的 id。刪完之後要找得回遞補上來的那一列,把焦點交給它。
 *
 * 用 `key` 而不是 `seq`:刪掉中間一列會讓後面的期數整批往前挪(`renumber()`),
 * 拿 `seq` 去找會找到「另一列」。
 */
function rowDomId(key: string): string {
  return `nq-term-${key}`;
}

/** 已開票的期別為什麼刪不掉。與後端 `replace_payment_terms()` 回的 409 同一句。 */
function invoicedIsLocked(seq: number): string {
  return `第 ${seq} 期已經開過發票,不可以刪掉;要改請先把發票作廢`;
}

const FIELD = quietControlClass();
const NUM_FIELD = quietControlClass({ numeric: true });
const READONLY_FIELD = quietControlClass({ readOnly: true });
const READONLY_NUM_FIELD = quietControlClass({ readOnly: true, numeric: true });

export interface PaymentTermsProps {
  quoteId: number;
  /**
   * 報價單總額。**用畫面上當下那個數字**(TotalsBar 的即時總計),不是資料庫裡
   * 那一份 —— 兩邊不同的時候,使用者看到的各期金額必須跟著他眼前的總計走,
   * 否則他會以為系統算錯了。存檔後後端會用它自己的總額再算一次。
   */
  total: Money;
  /** 後端目前存著的期別。整份換掉時(存檔、寄送、復原)重新帶進來。 */
  terms: QuotePaymentTerm[];
  /** 這張單現在改不動(財務唯讀或已封存)。看得到,但一個字都改不了。 */
  readOnly?: boolean;
  /** 為什麼改不動。反灰不可以只靠顏色,這一句是那幾顆灰掉的鈕留給人的唯一理由。 */
  readOnlyReason?: string;
  /** 存檔成功後把後端回的那一份交出去,讓編輯器手上的報價單跟著更新。 */
  onSaved?: (terms: QuotePaymentTerm[]) => void;
  /**
   * 這一區有沒有待存的改動。**往上回報是必要的**:付款條件走自己的端點、
   * 不進草稿,所以 `useQuoteDraft` 的 `dirty` 完全不知道有人改過比例或金額 ——
   * 抬頭那句「有未存檔的修改」不涵蓋它的話,使用者改完三期比例、抬頭卻告訴他
   * 沒有東西待存,他就放心跳走了,而那三筆改動無聲消失。
   */
  onDirtyChange?: (dirty: boolean) => void;
}

export function PaymentTerms({
  quoteId,
  total,
  terms,
  readOnly = false,
  readOnlyReason,
  onSaved,
  onDirtyChange,
}: PaymentTermsProps) {
  const headingId = useId();
  const [rows, setRows] = useState<TermRow[]>(() => terms.map(toRow));
  /**
   * 刪掉一列之後焦點要去哪(以 `key` 指名)。`null` = 沒有相鄰列了。
   *
   * 不安排的話,React 卸載那一列時焦點會掉回 `<body>`:鍵盤使用者要再刪一列,
   * 就得從整頁最上面重新 Tab 過側邊欄、工具列與所有明細欄位才回得到這張表,
   * 連刪兩列等於把整頁 Tab 兩遍。做法與明細那張表(`LineTable`)一字不差。
   */
  const focusAfterRemoval = useRef<string | null>(null);
  /** 同一個 key 可能連續被指名兩次,所以用一個只增不減的計數觸發 effect。 */
  const [removalTick, setRemovalTick] = useState(0);
  /** 沒有相鄰列時的去處:「加一期」那顆鈕 —— 那是刪完之後唯一做得了的事。 */
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const [saving, setSaving] = useState(false);
  /**
   * 錯誤留在畫面上,不用 toast。
   *
   * 錯誤裡帶著下一步要做什麼(「第 2 期不可以同時填比例與金額」),那是要讀得完
   * 的;飄過去三秒就消失的訊息等於沒說過,而使用者還得自己回頭數是第幾列。
   */
  const [error, setError] = useState("");

  /**
   * 「已經存到後端的那一份」長什麼樣。`dirty` 拿編輯中的列跟它比。
   *
   * **不直接比 `terms` prop**:存檔成功後最新的那一份是後端回來的那個
   * 陣列,而 `onSaved` 是選用的 —— 呼叫端沒接的話,prop 永遠停在舊值,
   * 這一區會在存完之後繼續宣稱有未存檔的修改,而那是最快讓人不再看提示的方式。
   */
  const [baseline, setBaseline] = useState<string>(() => fingerprint(terms.map(toFields)));

  // 整份報價單被換掉時(存檔、寄送、套範本、復原)重新帶一次。
  // `terms` 由編輯器用 state 持有,參考是穩定的,不會每 render 洗掉編輯中的內容。
  useEffect(() => {
    setRows(terms.map(toRow));
    setBaseline(fingerprint(terms.map(toFields)));
    setError("");
  }, [terms]);

  // 用 layout effect:要在瀏覽器畫下一格之前就把焦點放好,焦點環才不會閃一下。
  useLayoutEffect(() => {
    if (removalTick === 0) return;
    const key = focusAfterRemoval.current;
    focusAfterRemoval.current = null;
    const row = key === null ? null : document.getElementById(rowDomId(key));
    const next = row?.querySelector<HTMLElement>('[data-term-remove="true"]') ?? null;
    (next ?? addButtonRef.current)?.focus();
  }, [removalTick]);

  /**
   * 有沒有待存的改動。逐欄比對編輯中的列與後端那一份。
   *
   * 「改了又改回來」算沒改過(加一期又刪掉、把 20 改回 30),這是刻意的 ——
   * 一個只要碰過就不肯熄掉的提示,兩天之後就沒有人會看它了。
   */
  const dirty = useMemo(() => fingerprint(rows) !== baseline, [rows, baseline]);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const resolvable = useMemo(() => rows.map(toResolvable), [rows]);
  const amounts = useMemo(() => resolveAmounts(total, resolvable), [total, resolvable]);

  /** 比例那一欄的合計。沒有任何一期用比例填時是 null —— 那時談的不是比例。
      算式與 `resolveAmounts()` 的那道界限共用一支 `percentTotalOf()`:
      「超過 100% 就不吸尾差」與畫面上那行「差 -20%」必須是同一個判斷。 */
  const percentTotal = useMemo(() => {
    if (!resolvable.some((term) => term.percent !== null)) return null;
    return percentTotalOf(resolvable);
  }, [resolvable]);

  const amountTotal = useMemo(
    () => amounts.reduce((acc, amount) => acc.plus(toDecimal(amount)), ZERO),
    [amounts],
  );

  const hasInvoiced = rows.some(isInvoiced);
  const reason = readOnly ? (readOnlyReason ?? "這張報價單現在改不動") : undefined;

  const patchRow = (index: number, patch: Partial<TermRow>) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    setError("");
  };

  const addRow = () => {
    setRows((prev) => renumber([...prev, blankRow(nextSeq(prev))]));
    setError("");
  };

  const removeRow = (index: number) => {
    // 遞補到這個位置的是下一列;它是最後一列的話就退回前一列。
    const neighbour = rows[index + 1] ?? rows[index - 1] ?? null;
    focusAfterRemoval.current = neighbour?.key ?? null;
    setRows((prev) => renumber(prev.filter((_, i) => i !== index)));
    setError("");
    setRemovalTick((tick) => tick + 1);
  };

  const applyPreset = (preset: (typeof PRESETS)[number]) => {
    setRows(preset.rows.map((row, index) => blankRow(index + 1, { name: row.name, percent: row.percent })));
    setError("");
  };

  /**
   * 存檔前擋得住的那一種寫法。**訊息一律說得出是第幾期** ——「有一期填錯了」
   * 等於沒說,而一張單可以有很多期。與後端 `_reject_unusable_terms()` 同一句話:
   * 按鈕旁邊說的與真的送上去會收到的錯誤,必須講同一件事。
   *
   * **合計不是 100% 不在這裡**(§2.4):那是提示,不是錯誤。
   */
  const firstProblem = (): string => {
    for (const row of rows) {
      if (row.percent.trim() !== "" && row.amount.trim() !== "") {
        return `第 ${row.seq} 期不可以同時填比例與金額,請二擇一`;
      }
    }
    return "";
  };

  const save = async () => {
    const problem = firstProblem();
    if (problem !== "") {
      // 送出去只會換來一個 422,連一次往返都不必花。
      setError(problem);
      return;
    }
    setSaving(true);
    try {
      const payload: PaymentTermInput[] = rows.map((row) => ({
        seq: row.seq,
        name: row.name,
        percent: row.percent.trim() === "" ? null : toDecimal(row.percent).toFixed(),
        amount: row.amount.trim() === "" ? null : toAmountString(row.amount),
        due_date: row.due_date.trim() === "" ? null : row.due_date,
      }));
      const saved = await api.put<QuotePaymentTerm[]>(`/quotes/${quoteId}/payment-terms`, payload);
      const fresh = Array.isArray(saved) ? saved : [];
      setRows(fresh.map(toRow));
      // 後端回來的那一份就是新的「已經存到後端的樣子」—— 存完了就不該再說有
      // 東西待存,即使呼叫端沒有接 `onSaved`(prop 因此還停在舊值)。
      setBaseline(fingerprint(fresh.map(toFields)));
      setError("");
      onSaved?.(fresh);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "付款條件儲存失敗");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      aria-labelledby={headingId}
      className="flex flex-col gap-3 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] px-3 py-3 shadow-card"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Wallet aria-hidden="true" size={16} className="shrink-0 text-[var(--text-3)]" />
        <h3 id={headingId} className="m-0 text-sm font-semibold text-[var(--text)]">
          付款條件
        </h3>
        <span className="text-xs text-[var(--text-3)]">
          比例與金額二擇一;尾差落在最後一期,印在報價單總計下方
        </span>
        {/* 待存的改動要**寫出來**,不可以只靠一顆鈕變色:這一區與抬頭那顆
            「儲存」是兩件事,不說的話「改完了」與「還沒存」在畫面上長得一樣。 */}
        {dirty ? (
          <span className="text-xs font-medium text-[var(--warn)]">付款條件有未存檔的修改</span>
        ) : null}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {PRESETS.map((preset) => (
            <Button
              key={preset.label}
              size="sm"
              // 一鍵填入會把現有的列整批換掉。已經開過發票的期別換不得
              // (後端會回 409),所以這幾顆在那種單子上一律按不動並說出原因。
              disabled={readOnly || hasInvoiced}
              title={reason ?? (hasInvoiced ? "這張單已經有開過發票的期別,不能整批換掉" : undefined)}
              onClick={() => applyPreset(preset)}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </div>

      {readOnly ? (
        <div
          role="status"
          aria-label="付款條件權限"
          className="flex flex-wrap items-center gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface-2)] px-3 py-2"
        >
          <Lock aria-hidden="true" size={16} className="shrink-0 text-[var(--text-3)]" />
          <span className="min-w-0 flex-1 text-sm text-[var(--text)]">
            {`${reason}:付款條件看得到,但不能新增、修改或刪除。`}
          </span>
        </div>
      ) : null}

      {rows.length === 0 ? (
        <p className="m-0 text-sm text-[var(--text-2)]">
          還沒有付款條件。用上面三個常用組合一鍵填入,或按「加一期」自己排。
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table aria-label="付款期別" className="w-full min-w-[44rem] border-collapse text-left">
            <thead>
              <tr className="border-b border-[var(--border)] text-xs text-[var(--text-3)]">
                <th scope="col" className="px-2 py-1.5">
                  期別
                </th>
                <th scope="col" className="px-2 py-1.5">
                  名稱
                </th>
                <th scope="col" className="px-2 py-1.5 text-right">
                  比例 %
                </th>
                <th scope="col" className="px-2 py-1.5 text-right">
                  金額
                </th>
                <th scope="col" className="px-2 py-1.5">
                  預計請款日
                </th>
                <th scope="col" className="px-2 py-1.5 text-right">
                  本期金額
                </th>
                <th scope="col" className="w-10 px-2 py-1.5">
                  <span className="sr-only">操作</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const locked = readOnly || isInvoiced(row);
                const lockedReason = isInvoiced(row) ? invoicedIsLocked(row.seq) : reason;
                return (
                  <tr
                    key={row.key}
                    id={rowDomId(row.key)}
                    className="border-b border-[var(--border)] last:border-b-0"
                  >
                    <td className="whitespace-nowrap px-2 py-1 text-sm text-[var(--text-2)]">
                      第 {row.seq} 期
                      {isInvoiced(row) ? (
                        // 已開票不可以只用一顆灰掉的鈕表示,那一列本身要說得出來
                        <span className="ml-1 text-xs text-[var(--text-3)]">
                          (已開票 {formatMoney(row.invoiced_amount)})
                        </span>
                      ) : null}
                    </td>
                    <td className="min-w-[8rem] px-2 py-1">
                      <input
                        className={locked ? READONLY_FIELD : FIELD}
                        aria-label={`第 ${row.seq} 期名稱`}
                        placeholder="訂金 / 期中 / 尾款"
                        value={row.name}
                        readOnly={locked}
                        aria-disabled={locked || undefined}
                        title={locked ? lockedReason : undefined}
                        onChange={(event) => patchRow(index, { name: event.target.value })}
                      />
                    </td>
                    <td className="w-24 px-2 py-1">
                      <input
                        className={locked ? READONLY_NUM_FIELD : NUM_FIELD}
                        aria-label={`第 ${row.seq} 期比例`}
                        inputMode="decimal"
                        placeholder="30"
                        value={row.percent}
                        readOnly={locked}
                        aria-disabled={locked || undefined}
                        title={locked ? lockedReason : undefined}
                        onChange={(event) => patchRow(index, { percent: event.target.value })}
                      />
                    </td>
                    <td className="w-28 px-2 py-1">
                      <input
                        className={locked ? READONLY_NUM_FIELD : NUM_FIELD}
                        aria-label={`第 ${row.seq} 期金額`}
                        inputMode="decimal"
                        placeholder="講定的數字"
                        value={row.amount}
                        readOnly={locked}
                        aria-disabled={locked || undefined}
                        title={locked ? lockedReason : undefined}
                        onChange={(event) => patchRow(index, { amount: event.target.value })}
                      />
                    </td>
                    <td className="w-40 px-2 py-1">
                      <input
                        className={locked ? READONLY_FIELD : FIELD}
                        aria-label={`第 ${row.seq} 期預計請款日`}
                        type="date"
                        value={row.due_date}
                        readOnly={locked}
                        aria-disabled={locked || undefined}
                        title={locked ? lockedReason : undefined}
                        onChange={(event) => patchRow(index, { due_date: event.target.value })}
                      />
                    </td>
                    <td
                      aria-label={`第 ${row.seq} 期本期金額`}
                      className="tabular w-32 px-2 py-1 text-right text-sm font-medium text-[var(--text)]"
                    >
                      {formatMoney(amounts[index] ?? "0.00")}
                    </td>
                    <td className="w-10 px-2 py-1 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        // 刪完之後靠它找回遞補上來的那一列(見 `focusAfterRemoval`)
                        data-term-remove="true"
                        aria-label={`刪除第 ${row.seq} 期`}
                        icon={<Trash2 aria-hidden="true" size={16} />}
                        disabled={locked}
                        title={locked ? lockedReason : undefined}
                        onClick={() => removeRow(index)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 ? (
        <div
          role="status"
          aria-label="付款條件合計"
          className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[var(--text-2)]"
        >
          {percentTotal === null ? null : (
            <span>
              比例合計 {percentTotal.toFixed()}%
              {percentTotal.equals(HUNDRED) ? null : (
                // 差多少直接講出來,不要讓人自己減。**只提示,不擋存檔**(§2.4)。
                <span className="ml-1 text-[var(--warn)]">
                  ,差 {HUNDRED.minus(percentTotal).toFixed()}%(還是存得下去)
                </span>
              )}
            </span>
          )}
          <span className="tabular">
            各期合計 {formatMoney(amountTotal.toFixed(2))}(報價總額 {formatMoney(total)})
          </span>
        </div>
      ) : null}

      {error === "" ? null : (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          ref={addButtonRef}
          size="sm"
          icon={<Plus aria-hidden="true" size={16} />}
          disabled={readOnly}
          title={reason}
          onClick={addRow}
        >
          加一期
        </Button>
        <Button
          size="sm"
          // 有待存的改動時才是主要動作 —— 一顆永遠長得像主要動作的鈕,
          // 說不出「現在該按它」。
          variant={dirty ? "primary" : "secondary"}
          // 合計不是 100% 照樣存得下去(§2.4)—— 擋在這裡只會逼人先亂填一個數字。
          disabled={readOnly}
          title={reason}
          loading={saving}
          onClick={() => {
            void save();
          }}
        >
          儲存付款條件
        </Button>
      </div>
    </section>
  );
}

/** 下一期的期數。刪過中間幾列之後仍然接得下去,不會撞到還留著的那幾期。 */
function nextSeq(rows: TermRow[]): number {
  return rows.reduce((max, row) => Math.max(max, row.seq), 0) + 1;
}
